import React, {useMemo, useRef, useState, useEffect} from 'react';
import {Card, DatePicker, Divider, InputNumber, Statistic} from "antd";
import Form, {FormInputComponent} from "../../components/form/Form";
import {useOpportunity} from "../../providers/OpportunityProvider";
import SpinLoader from "../../components/SpinLoader";
import {Info, Package, TrendingUp} from 'lucide-react';

const FixedFeePayoutEditTab = ({
    onUpdate = async () => true,
    showSteps,
    getStepActions = false, ...props
}) => {

    const [isCollapseActive, setIsCollapseActive] = useState(true);
    const {
        opportunity,
        isDisabled,
        changeProps,
        isUpdating,
        onRequest
    } = useOpportunity();

    let talentOpp = TB.cloneDeep(opportunity.job_candidate.talent_opps[0]);

    const editable = opportunity.canEditProposal();


    const [formModel, setFormModel] = useState({
        ...TB.pick({
            ...talentOpp
        }, [
            'total_hours',
            'hourly_rate',
            'position_deliverables',
            'has_accepted_proposed_payout',
        ]),
        payout_amount: opportunity.job_candidate?.estimated_payout_amount || opportunity.project_position?.payout_amount,
    });

    const proposedPayout = useMemo(() => {
        const hours = parseInt(TB.sumBy(formModel.position_deliverables, 'total_hours'));
        const rate = parseFloat(formModel.hourly_rate);

        if (TB.isNaN(hours) || TB.isNaN(rate)) {
            return 0;
        }

        return Math.ceil(hours * rate);
    }, [
        formModel?.total_hours,
        formModel?.hourly_rate,
        ...TB.map(formModel.position_deliverables || [], 'total_hours'),
        formModel.has_accepted_proposed_payout
    ])

    const hasFormModelChanged = useMemo(() => {
        if (formModel.has_accepted_proposed_payout !== talentOpp.has_accepted_proposed_payout
            || formModel.hourly_rate !== talentOpp.hourly_rate
        ) {
            return true
        }
        if (formModel.payout_amount !== opportunity.project_position.payout_amount) {
            return true;
        }
        let hasFormChange = false;
        TB.map(formModel.position_deliverables, del => {
            let matchedDel = TB.find(talentOpp.position_deliverables, ['id', del.id]);
            if (matchedDel && matchedDel.total_hours !== del.total_hours) {
                hasFormChange = true;
            }
            if (matchedDel && matchedDel.payout_amount !== del.payout_amount) {
                hasFormChange = true;
            }
            if (!TB.moment(del.target_delivery_at).isSame(matchedDel.target_delivery_at, 'day')) {
                hasFormChange = true;
            }
        })
        return hasFormChange;
    }, [
        formModel?.total_hours,
        formModel?.hourly_rate,
        ...TB.map(formModel.position_deliverables || [], 'total_hours'),
        ...TB.map(formModel.position_deliverables || [], 'target_delivery_at'),
        formModel.has_accepted_proposed_payout
    ])

    const formRef = useRef(null);

    const resetForm = async () => {
        return formRef.current?.forceReset(talentOpp)
    }


    const isFullBreakdown = opportunity.project_position.fixedFeePricingStrategyIsFullBreakdown();
    const isTotalPayout = opportunity.project_position.fixedFeePricingStrategyIsTotalPayout();
    const isCostPerDeliverable = opportunity.project_position.fixedFeePricingStrategyIsPayoutByDeliverable();

    // Project data
    const projectBudget = parseFloat(opportunity.project_position.estimated_payout);



    // Proposal type state
    const [proposalType, setProposalType] = useState(
        isFullBreakdown ? 'fixed-fee-hourly' : (
            isTotalPayout ? 'fixed-fee-total' : 'fixed-fee-deliverable')
    );


    // State for Fixed Fee proposals
    const [expandedDescriptions, setExpandedDescriptions] = useState(TB.map(formModel.position_deliverables || [], () => false));

    // Calculated values
    const [totalProposedPayout, setTotalProposedPayout] = useState(0);
    const [isOverBudget, setIsOverBudget] = useState(false);

    const submitForm = async () => {
        let invalid = formRef.current.forceValidate(formModel);
        if (invalid) {
            console.log('invalid', invalid)
            return;
        }
        let totalHours = formModel.total_hours;
        if (formModel.has_accepted_proposed_payout) {
            totalHours = TB.sumBy(formModel.position_deliverables, 'total_hours')
        }

        // Set payout amount based on strategy
        let payout_amount = formModel.payout_amount;

        if (isFullBreakdown) {
            payout_amount = TB.sumBy(formModel.position_deliverables, 'total_hours') * formModel.hourly_rate;
        }
        if (isCostPerDeliverable) {
            payout_amount = TB.sumBy(formModel.position_deliverables, 'payout_amount')
        }
        const body = {
            position_deliverables: TB.map(formModel.position_deliverables, del => {
                let matchedDel = TB.find(talentOpp.position_deliverables, ['id', del.id]);
                if (!del.target_delivery_at) {
                    del.target_delivery_at = matchedDel.target_delivery_at || opportunity.project_position.ended_at;
                }
                if (isTotalPayout) {
                    del.payout_amount = null;
                    del.total_hours = null;
                }else if (isFullBreakdown) {
                    del.payout_amount = null;
                }
                return del;
            }),
            job_candidate: {
                total_hours: isFullBreakdown ? TB.sumBy(formModel.position_deliverables, 'total_hours') : totalHours,
                hourly_rate: formModel.hourly_rate,
            },
            payout_amount,
            talent_proposed_at: opportunity.talent_proposed_at || new Date()
        }
        console.log(body, 'on submit body')
        let success = await onUpdate(body);
        if (success) {
            await resetForm();
        }
    }

    useEffect(() => {
        let total = 0;

        if (proposalType === 'fixed-fee-total') {
            total = formModel.payout_amount;
        } else if (proposalType === 'fixed-fee-deliverable') {
            total = TB.sumBy(formModel.position_deliverables, del => parseFloat(del.payout_amount || 0));
        } else if (proposalType === 'fixed-fee-hourly') {
            const totalHours = TB.sumBy(formModel.position_deliverables, del => parseInt(del.total_hours || 0));
            total = totalHours * formModel.hourly_rate;
        }

        setTotalProposedPayout(total);
        setIsOverBudget(Math.round(total) > Math.round(projectBudget));
    }, [proposalType, formModel.payout_amount,
        TB.map(formModel.position_deliverables, 'payout_amount'),
        TB.map(formModel.position_deliverables, 'total_hours'),
        formModel.hourly_rate, projectBudget]);


    const toggleDescriptionExpansion = (index) => {
        const newExpanded = [...expandedDescriptions];
        newExpanded[index] = !newExpanded[index];
        setExpandedDescriptions(newExpanded);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    const formatHourlyRate = (rate) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(rate);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const proposalTypes = [
        { value: 'fixed-fee-total', label: 'Fixed Fee - Total Cost' },
        { value: 'fixed-fee-deliverable', label: 'Fixed Fee - Cost Per Deliverable' },
        { value: 'fixed-fee-hourly', label: 'Fixed Fee - Full Hourly Breakdown' }
    ];

    const percentageOfBudget = ((totalProposedPayout / projectBudget) * 100).toFixed(1);
    const totalHours = proposalType === 'fixed-fee-hourly' ? TB.sumBy(formModel.position_deliverables || [], del => parseInt(del.total_hours || 0)) : null;

    const getProposalTypeLabel = () => {
        return proposalTypes.find(type => type.value === proposalType)?.label;
    };

    return (
        <SpinLoader isLoading={isUpdating}>
            {/* <h2 className="text-warning-darker text-xl pb-4">Fixed Fee</h2> */}
            <h2 className="text-xl pb-4">
                Type of Contract: <span className="text-warning-darker">Fixed Fee</span>
            </h2>

            {getStepActions && getStepActions(
                // 'Please confirm your 1) bill rate, 2) hours per deliverable and 3) payout to work on this project',
                // 'Please confirm your total proposed cost for this project',
                'Please confirm your proposed price for this project below',
                submitForm
            )}

            <div className="opp-talent-proposal-form opp-member-proposal-form">
                <Form
                    ref={formRef}
                    background={"transparent"}
                    // mapping={mapping}
                    minimal={true}
                    submitButton={false}
                    isDisabled={isDisabled}
                    onSubmit={submitForm}
                    model={formModel}
                    fields={[]}
                    // className={
                    //     TB.mapClass({
                    //         'no-edit': !editable
                    //     })
                    // }
                    onChange={async (model) => {
                        // let has_accepted_proposed_payout = formModel.has_accepted_proposed_payout;
                        let updatedModel = { ...formModel }
                        for (let [key, value] of Object.entries(model)) {
                            updatedModel = TB.set(updatedModel, key, value);
                        }
                        await setFormModel(updatedModel);
                    }}>

                    <div className="mx-auto">


                        <div className="flex flex-row justify-between items-start mb-4">
                            <div className="max-w-2xl ">
                                <p className="text-sm text-gray-600">
                                    Review the project details and propose your preferred payout structure.
                                </p>

                            </div>
                        </div>


                        <div className="flex flex-row items-stretch justify-between gap-6 mb-6">
                            {/* Conditional Explanation */}
                            <div className="max-w-2xl bg-purple-50 border border-purple-200 p-4">
                                <div className="flex items-start">
                                    <Info className="w-5 h-5 text-purple-600 mr-3 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="text-base text-purple-800 font-semibold mb-1">
                                            {isTotalPayout ? 'Fixed Fee Pricing' : 'Deliverable Based Pricing'}
                                        </p>
                                        <p className="text-purple-700 text-sm">
                                            Per client request, this is a deliverable-based project with a fixed scope. Payment is based on completed deliverables rather than time worked.
                                            {proposalType === 'fixed-fee-total' && ' Propose your total project cost.'}
                                            {proposalType === 'fixed-fee-deliverable' && ' Set individual prices for each deliverable.'}
                                            {proposalType === 'fixed-fee-hourly' && ' Provide hourly estimates for each deliverable with your desired rate.'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <Card bordered={false} className="bg-gray-100 min-w-[200px] self-start ml-auto" size="small">
                                <Statistic
                                    title="Project Budget"
                                    value={TB.formatNumber(opportunity.project_position.estimated_payout, 2)}
                                    formatter={value => TB.formatCurrency(value, false)}
                                />

                                <p className="text-xxs font-semibold text-warning-darker">Fixed Fee</p>
                            </Card>
                        </div>



                        {/* Project Deliverables Overview */}
                        <div className="bg-gray-50 p-6 mb-6">
                            <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                                <Package className="w-5 h-5 mr-2 text-green-600" />
                                Project Deliverables
                            </h3>

                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse">
                                    <thead>
                                    <tr className="border-b border-gray-300 align-top">
                                        <th className="text-left py-3 px-2">
                                            <p className="whitespace-nowrap font-semibold text-sm text-gray-700">
                                                Deliverable
                                            </p>
                                        </th>
                                        <th className="text-center py-3 px-2 min-w-28">
                                            <p className="whitespace-nowrap font-semibold text-sm text-gray-700">
                                                Requested Date
                                            </p>
                                        </th>
                                        <th className="text-center py-3 px-2 min-w-36">
                                            <p className="whitespace-nowrap font-semibold text-sm text-gray-700">
                                                My Delivery Date
                                            </p>
                                        </th>
                                        {proposalType === 'fixed-fee-deliverable' && (
                                            <th className="flex flex-col items-center text-center py-3 px-2 min-w-32">
                                                <div className="flex flex-col items-center text-center">
                                                    <p title="Proposed Price" className="font-semibold whitespace-nowrap text-gray-700 text-sm">
                                                        Proposed Price
                                                    </p>
                                                    <p className="text-xxs text-red-500">Required</p>
                                                </div>
                                            </th>
                                        )}
                                        {proposalType === 'fixed-fee-hourly' && (
                                            <>
                                                <th className="py-3 px-2 min-w-24 max-w-24">
                                                    <div className="flex flex-col items-center text-center">
                                                        <p title="My Hours" className="font-semibold whitespace-nowrap text-gray-700 text-sm">
                                                            My Hours
                                                        </p>
                                                        <p className="text-xxs text-red-500">Required</p>

                                                    </div>
                                                </th>
                                                <th className="text-center py-3 px-2 min-w-32">
                                                    <p title="Proposed Price" className="font-semibold whitespace-nowrap text-gray-700 text-sm">
                                                        Proposed Price
                                                    </p>
                                                </th>
                                            </>
                                        )}
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {formModel.position_deliverables.map((deliverable, index) => {
                                        let matchedDel = TB.find(opportunity.project_position.deliverables, ['id', deliverable.id]);

                                        return (
                                            <tr key={deliverable.id} className="border-b border-gray-200">
                                                <td className="py-4 px-2 min-w-40">
                                                    <div>
                                                        <h4 className="font-semibold text-sm text-gray-900 mb-1">{matchedDel.name}</h4>
                                                        <div>
                                                            <p
                                                                className={`text-xs text-gray-900 ${expandedDescriptions[index] ? '' : ''}`}
                                                                style={expandedDescriptions[index] ? {} : {
                                                                    display: '-webkit-box',
                                                                    WebkitLineClamp: 2,
                                                                    WebkitBoxOrient: 'vertical',
                                                                    overflow: 'hidden'
                                                                }}
                                                            >
                                                                {matchedDel.description + matchedDel.description + matchedDel.description + matchedDel.description + matchedDel.description}
                                                            </p>
                                                            {(matchedDel.description +matchedDel.description + matchedDel.description + matchedDel.description + matchedDel.description).length > 60 && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => toggleDescriptionExpansion(index)}
                                                                    className="text-info text-xs font-medium mt-1"
                                                                >
                                                                    {expandedDescriptions[index] ? 'Read Less' : 'Read More'}
                                                                </button>
                                                            )}
                                                        </div>
                                                        {!!matchedDel.total_hours && (
                                                            <div className="text-xs text-gray-500 mt-2">
                                                                <span>Client estimated: </span>
                                                                <span className="text-success-dark font-medium">
                                                                    {matchedDel.total_hours} hours
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="py-4 px-2 text-center">
                                                    <div className="text-sm text-gray-600">
                                                        {formatDate(matchedDel.target_delivery_at)}
                                                    </div>
                                                </td>

                                                <td className="py-4 px-2 text-center">
                                                    <FormInputComponent
                                                        type="antd-input"
                                                        label={false}
                                                        className="relative mb-0 max-w-36 min-w-36 mx-auto"
                                                        invalidStyles={{ top: '100%', position: 'absolute', fontSize: 10 }}
                                                        formatAsDate
                                                        fieldName={`position_deliverables[${index}].target_delivery_at`}
                                                        disabled={false}
                                                        value={deliverable.target_delivery_at ? TB.moment(deliverable.target_delivery_at) : undefined}
                                                    >
                                                        <DatePicker
                                                            disabledDate={date => false}
                                                            format="ll"
                                                            defaultValue={deliverable.target_delivery_at}
                                                            // size="small"
                                                        />
                                                    </FormInputComponent>
                                                </td>
                                                {proposalType === 'fixed-fee-deliverable' && (
                                                    <td className="py-4 px-2 text-center">
                                                        <FormInputComponent
                                                            type="antd-input"
                                                            label={false}
                                                            className="relative mb-0 max-w-36 min-w-36 mx-auto"
                                                            fieldName={`position_deliverables[${index}].payout_amount`}
                                                            // disabled={formModel.has_accepted_proposed_payout || isDisabled}
                                                            // disabled={(isFullBreakdown && formModel.has_accepted_proposed_payout) || isDisabled}
                                                            value={deliverable.payout_amount}
                                                            required
                                                            min={1}
                                                        >
                                                            <InputNumber
                                                                precision={0}
                                                                controls={false}
                                                                addonBefore="$"
                                                                step={1}
                                                                defaultValue={Number.parseInt(deliverable.payout_amount).toFixed(0)}
                                                            />
                                                        </FormInputComponent>

                                                    </td>
                                                )}
                                                {proposalType === 'fixed-fee-hourly' && (
                                                    <>
                                                        <td className="py-4 px-2 text-center">

                                                            <FormInputComponent
                                                                type="antd-input"
                                                                label={false}
                                                                className="relative mb-0 max-w-36 min-w-36"
                                                                invalidStyles={{ top: '100%', position: 'absolute', fontSize: 10 }}
                                                                fieldName={`position_deliverables[${index}].total_hours`}
                                                                // disabled={formModel.has_accepted_proposed_payout || isDisabled}
                                                                // disabled={(isFullBreakdown && formModel.has_accepted_proposed_payout) || isDisabled}
                                                                value={deliverable.total_hours}
                                                                // required={!formModel.has_accepted_proposed_payout}
                                                                min={1}
                                                            >
                                                                <InputNumber
                                                                    precision={0}
                                                                    controls={false}
                                                                    addonAfter="hours"
                                                                    className="[&_.ant-input-number-input]:text-center"
                                                                    step={1}
                                                                    defaultValue={deliverable.total_hours}
                                                                />
                                                            </FormInputComponent>
                                                        </td>
                                                        <td className="py-4 px-2 text-center">
                                                            <div className="font-semibold text-sm text-gray-900">
                                                                {formatCurrency(deliverable.total_hours * formModel.hourly_rate)}
                                                            </div>
                                                            <div className="mt-1 text-xxs text-gray-500">
                                                                {deliverable.total_hours}hr × {formatHourlyRate(formModel.hourly_rate)}
                                                            </div>
                                                        </td>
                                                    </>
                                                )}
                                            </tr>
                                        )
                                    })}
                                    </tbody>
                                    {/* Footer with totals */}
                                    <tfoot>
                                    <tr className="border-t-2 border-gray-400 bg-gray-100">
                                        <td className="py-3 px-2 text-center">
                                            {/* Empty cell for del column */}
                                        </td>

                                        <td className="py-3 px-2 text-center">
                                            {/* Empty cell for est date column */}
                                        </td>


                                        <td className="py-3 px-2 font-semibold text-sm text-right text-gray-900">
                                            TOTALS
                                        </td>

                                        {proposalType === 'fixed-fee-deliverable' && (
                                            <td className="py-3 px-2 text-center">
                                                <div className="font-bold text-sm text-gray-900">
                                                    {formatCurrency(TB.sumBy(formModel.position_deliverables || [], del => parseFloat(del.payout_amount || 0)))}
                                                </div>
                                            </td>
                                        )}


                                        {proposalType === 'fixed-fee-hourly' && (
                                            <>
                                                <td className="py-3 px-2 text-center">
                                                    <div className="font-bold text-sm text-gray-900 flex items-center justify-center">
                                                        {totalHours} hours
                                                    </div>
                                                </td>
                                                <td className="py-3 px-2 text-center">
                                                    <div className="font-bold text-sm text-gray-900">
                                                        {formatCurrency(totalHours * formModel.hourly_rate)}
                                                    </div>
                                                    <div className="text-xs text-gray-600">
                                                        {totalHours}h × {formatHourlyRate(formModel.hourly_rate)}
                                                    </div>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>

                        <Divider/>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            My Proposal
                        </h3>

                        <div className="flex flex-col gap-2 max-w-2xl p-6 mb-6 border-2 bg-blue-50 border-blue-100">
                            {/* Dynamic Input Section */}
                            {proposalType === 'fixed-fee-total' && (
                                // Fixed Fee Total Cost Input
                                <div>
                                    <label className="block font-medium text-gray-900 mb-3">
                                        Your Proposed Total Project Cost
                                    </label>
                                    <FormInputComponent
                                        fieldName="payout_amount"
                                        label={false}
                                        type="antd-input"
                                        value={formModel.payout_amount}
                                        placeholder={Number.parseInt(formModel.payout_amount).toFixed(0)}
                                    >
                                        <InputNumber
                                            className="w-full text-lg font-medium"
                                            prefix="$"
                                            step={1}
                                            controls={false}
                                            precision={2}
                                            size="large"/>

                                    </FormInputComponent>

                                </div>
                            )}

                            {proposalType === 'fixed-fee-hourly' && (
                                // Fixed Fee Hourly Rate Input
                                <div>
                                    <label className="block font-medium text-gray-900 mb-3">
                                        My Bill Rate
                                    </label>
                                    <FormInputComponent
                                        fieldName="hourly_rate"
                                        label={false}
                                        type="antd-input"
                                        value={formModel.hourly_rate}
                                        placeholder={opportunity.project_position.estimated_rate}
                                    >
                                        <InputNumber
                                            className="w-full text-lg font-medium"
                                            prefix="$"
                                            step={1}
                                            controls={false}
                                            precision={2}
                                            size="large"/>

                                    </FormInputComponent>
                                </div>
                            )}

                            <div className="pt-4 border-t border-gray-200">
                                <div className="text-xs text-gray-600 space-y-1">
                                    <div>Payment based on deliverable completion: <b>{opportunity.project_position.deliverables.length}</b> deliverables</div>
                                    {proposalType === 'fixed-fee-total' && (
                                        <div>Payment of <b>{formatCurrency(totalProposedPayout)}</b>s upon project completion</div>
                                    )}
                                    {proposalType === 'fixed-fee-deliverable' && (
                                        <div>Individual deliverable payments: <b>{TB.map(formModel.position_deliverables || [], del => formatCurrency(parseFloat(del.payout_amount || 0))).join(', ')}</b></div>
                                    )}
                                    {proposalType === 'fixed-fee-hourly' && (
                                        <>
                                            <div>Total time commitment: <b>{totalHours} hours</b> across all deliverables</div>
                                            <div>Effective hourly rate: <b>{formatHourlyRate(formModel.hourly_rate)}</b></div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>


                        {/* Proposal Summary */}
                        <div className={`max-w-2xl p-6 mb-6 border-2 bg-blue-50 border-blue-100`}>
                            <h3 className="text-base font-medium text-gray-900 mb-4 flex items-center">
                                <TrendingUp className="w-5 h-5 mr-2 text-blue-600" />
                                Your Proposal Summary
                            </h3>

                            <div className="flex justify-center mb-4">
                                <div className="text-center">
                                    <div className="text-xs text-gray-600 mb-1">Total proposed payout</div>
                                    <div className={`text-2xl font-bold text-green-600`}>
                                        {formatCurrency(totalProposedPayout)}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        <b>{percentageOfBudget}%</b> of project budget
                                    </div>
                                </div>
                            </div>

                        </div>

                        {/* Warning for over-budget */}

                        {isOverBudget && (
                            <div className="max-w-2xl bg-yellow-50 border border-yellow-200 p-4 mb-6">
                                <div className="flex items-start">
                                    <Info className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="text-base text-yellow-700 font-semibold mb-1">
                                            Proposal exceeds estimated project budget
                                        </p>
                                        <p className="text-yellow-700 text-sm">
                                            {`Your total proposed payout of ${formatCurrency(totalProposedPayout)} exceeds the estimated project budget by ${formatCurrency(totalProposedPayout - projectBudget)}. Consider adjusting your pricing to align with the project budget.`}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>
                    {editable && !showSteps && (
                        <div className="mt-3 flex flex-row justify-end">
                            <button type="submit" className="btn btn-primary mr-3"
                                    disabled={isDisabled || !hasFormModelChanged}>
                                Save
                            </button>
                            <button className="btn btn-outline-dark"
                                    type="button" onClick={resetForm}
                                    disabled={isDisabled || !hasFormModelChanged}>
                                Cancel
                            </button>
                        </div>
                    )}

                </Form>


            </div>
        </SpinLoader>
    )
};

export default FixedFeePayoutEditTab;